import React, { useCallback, useEffect, useState } from 'react';
import { FaRegFilePdf } from 'react-icons/fa6';
import { buildApiUrl } from '../../utils/apiUrl';
import { downloadPdfById } from '../../utils/downloadPdf';
import '../../assets/scss/orders.scss';

const PAGE_SIZE = 25;

const Orders = () => {
	const [orders, setOrders] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [error, setError] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [query, setQuery] = useState('');
	const [zippingId, setZippingId] = useState(null);

	const fetchOrders = useCallback(async () => {
		try {
			const params = new URLSearchParams({
				page: String(page),
				pageSize: String(PAGE_SIZE),
			});
			if (query) {
				params.set('q', query);
			}
			const response = await fetch(
				`${buildApiUrl('pdfs/all-pdfs')}?${params.toString()}`,
				{ credentials: 'include' }
			);
			const data = await response.json();
			if (!response.ok) {
				setError(data.error || 'Kunne ikke hente ordre.');
				setOrders([]);
				return;
			}
			setOrders(Array.isArray(data.items) ? data.items : []);
			setTotal(Number(data.total) || 0);
			setError(null);
		} catch (err) {
			console.error('Error fetching orders:', err);
			setError('An error occurred while fetching orders.');
		}
	}, [page, query]);

	useEffect(() => {
		fetchOrders();
	}, [fetchOrders]);

	useEffect(() => {
		const handle = window.setTimeout(() => {
			setPage(1);
			setQuery(searchTerm.trim());
		}, 400);
		return () => window.clearTimeout(handle);
	}, [searchTerm]);

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const handleDownloadPdf = async (event, pdf) => {
		event.preventDefault();
		try {
			await downloadPdfById(pdf.id, pdf.pdf_name);
		} catch (err) {
			console.error(err);
			setError('Kunne ikke laste ned PDF.');
		}
	};

	const handleDownloadAttachment = async (pdfId) => {
		if (zippingId) {
			return;
		}
		setError(null);
		setZippingId(pdfId);
		try {
			const response = await fetch(
				buildApiUrl(`form/download-attachments/${pdfId}`),
				{ credentials: 'include' }
			);

			if (response.ok) {
				const blob = await response.blob();
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', `attachments_${pdfId}.zip`);
				document.body.appendChild(link);
				link.click();
				link.parentNode.removeChild(link);
				window.URL.revokeObjectURL(url);
			} else {
				console.error(
					'Failed to download attachments:',
					response.statusText
				);
				setError('Kunne ikke laste ned vedlegg.');
			}
		} catch (err) {
			console.error('Error downloading attachments:', err);
			setError('Kunne ikke laste ned vedlegg.');
		} finally {
			setZippingId(null);
		}
	};

	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	return (
		<div className='orderData'>
			<h1>Alle ordre sendt inn</h1>
			{error && <p className='error'>{error}</p>}
			<input
				type='search'
				placeholder='Søk etter navn eller PDF...'
				value={searchTerm}
				onChange={handleSearchChange}
				className='searchInput'
				aria-label='Søk etter navn eller PDF'
			/>
			<div className='ordersList'>
				{orders.length > 0 ? (
					<table>
						<thead>
							<tr>
								<th>Navn</th>
								<th>PDF</th>
								<th>Opprettet</th>
								<th>Attachments</th>
							</tr>
						</thead>
						<tbody>
							{orders.map((order) => (
								<tr key={order.id}>
									<td>{`${order.first_name} ${order.last_name}`}</td>
									<td>
										<div className='row'>
											<FaRegFilePdf size='20' />
											<a
												href={buildApiUrl(
													`pdfs/${order.id}/file`
												)}
												onClick={(event) =>
													handleDownloadPdf(
														event,
														order
													)
												}
											>
												{order.pdf_name}
											</a>
										</div>
									</td>
									<td>
										{new Date(
											order.created_at
										).toLocaleString()}
									</td>
									<td>
										{order.has_attachments && (
											<button
												type='button'
												className='download-attachments'
												disabled={Boolean(zippingId)}
												aria-busy={zippingId === order.id}
												aria-live={
													zippingId === order.id
														? 'polite'
														: undefined
												}
												onClick={() =>
													handleDownloadAttachment(
														order.id
													)
												}
											>
												{zippingId === order.id ? (
													<>
														<span
															className='download-attachments__spinner'
															aria-hidden='true'
														/>
														Klargjør nedlasting
													</>
												) : (
													'Last ned vedlegg'
												)}
											</button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				) : (
					<p>Ingen bestillinger funnet.</p>
				)}
			</div>
			{total > PAGE_SIZE ? (
				<nav className='orders-pagination' aria-label='Sidetall'>
					<button
						type='button'
						disabled={page <= 1}
						onClick={() => setPage((p) => Math.max(1, p - 1))}
					>
						Forrige
					</button>
					<span>
						Side {page} av {totalPages} ({total} ordre)
					</span>
					<button
						type='button'
						disabled={page >= totalPages}
						onClick={() =>
							setPage((p) => Math.min(totalPages, p + 1))
						}
					>
						Neste
					</button>
				</nav>
			) : null}
		</div>
	);
};

export default Orders;
